import { formatNumber } from '@trmediaab/zebra-utils';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { bool, object } from 'prop-types';

import BetType from 'atg/api/BetType';
import { time } from 'atg/utils/Utils';
import { TIMEZONE_LABEL } from 'shared/constants/AppConstants';
import { formatMoney } from 'shared/utils/money';
import { getText } from 'shared/utils/Text';

// TODO - Throttle?
export const createBetData = api => {
  const dataset = {};

  let betType;
  const betInformation = api.betInformation;
  const stake = api.getStake();
  const betConfig = betInformation && betInformation.getBetConfig();

  if (betConfig) {
    const betKey = betInformation.getBetKey();
    if (betKey) {
      const betTypeCode = betKey.getBetType();
      betType = BetType.getBetType(betTypeCode);
    }
  }

  if (stake) {
    // Rows or combos
    const unreducedRows = stake.getUnreducedRows();
    const reducedRows = stake.getReducedRows();
    dataset.rows = {};
    dataset.rows.unreduced = unreducedRows ? unreducedRows : null;
    dataset.rows.reduced = unreducedRows && reducedRows ? reducedRows : null;
    dataset.rows.reducedPercent =
      100 - Math.round((100 * reducedRows) / unreducedRows) || 0;
    dataset.rows.rowsType = (betType && betType.getStakeRowsType()) || 'rows';

    // Price
    const price = stake.getStake();
    const bongFee = stake.getBongFee();
    const priceSum = price?.getSum() || 0;
    const rawPrice = priceSum - bongFee;
    if (rawPrice > 0) {
      dataset.formattedPrice = formatMoney(rawPrice, price.getCurrency());
      if (bongFee > 0) {
        dataset.formattedPrice += getText(
          'bong-fee',
          formatMoney(bongFee, price.getCurrency()),
        );
      }
    }

    // Number of counpons
    dataset.numberOfCoupons = stake.getNumberOfCoupons();
  }

  // Turnover
  const resultInformation = api.resultInformation;
  const gameInformation = api.gameInformation;

  let turnover;
  let turnoverTimetamp;

  if (gameInformation) {
    turnover = gameInformation.getTurnover();
    if (turnover) {
      turnoverTimetamp = gameInformation.getTimestamp();
    }
  }

  if (!turnover && resultInformation) {
    turnover = resultInformation.getTurnover();
    if (turnover) {
      turnoverTimetamp = resultInformation.getTimestamp();
    }
  }

  if (turnover) {
    dataset.formattedTurnover = formatMoney(
      turnover.getSum(),
      turnover.getCurrency(),
    );

    if (turnoverTimetamp) {
      dataset.formattedTurnover += ` (${time(
        turnoverTimetamp,
        true,
      )} ${TIMEZONE_LABEL})`;
    }
  }

  // Stake and systems
  if (betType) {
    dataset.stake = betType.hasStake() ? betConfig.getStake() : null;
    dataset.systems = betType.hasNumberOfSystems()
      ? stake.getNumberOfSystems()
      : null;
  }

  return dataset;
};

/* eslint-disable react/prop-types */
const LabelCell = ({ text }) => (
  <th>
    <div className="header">{text}</div>
  </th>
);
const ValueCell = ({ value }) => (
  <td>
    <div className="value">{value || '-'}</div>
  </td>
);
/* eslint-enable react/prop-types */

// Short rows info visible on narrow screen
const narrowRowsInfo = betData => [
  <LabelCell
    key="label1"
    text={getText(`system-info-label_${betData.rows.rowsType}`)}
  />,
  <ValueCell
    key="value1"
    value={
      betData.rows.unreduced &&
      betData.rows.reduced &&
      `${betData.rows.reduced} (${betData.rows.reducedPercent}%)`
    }
  />,
];

// Unreduced rows
const unreducedRows = betData => [
  <LabelCell
    key="label2"
    text={getText('system-info-label-unreduced-label')}
  />,
  <ValueCell
    key="value2"
    value={
      betData.rows.unreduced &&
      getText(
        `system-info_${betData.rows.rowsType}`,
        formatNumber(betData.rows.unreduced),
      )
    }
  />,
];

// Coupons
const coupons = betData => [
  <LabelCell key="label3" text={getText('system-info-label-coupons')} />,
  <ValueCell key="value3" value={formatNumber(betData.numberOfCoupons)} />,
];

// Price
const price = betData => [
  <LabelCell key="label4" text={getText('system-info-label-price')} />,
  <ValueCell key="value4" value={betData.formattedPrice} />,
];

// Short price info visible on narrow screen
const narrowPrice = betData => [
  <LabelCell key="label5" text={getText('system-info-label-price-short')} />,
  <ValueCell key="value5" value={betData.formattedPrice} />,
];

// Reduced rows
const reducedRows = betData => [
  <LabelCell key="label6" text={getText('system-info-label-reduced-label')} />,
  <ValueCell
    key="value6"
    value={
      betData.rows.reduced &&
      `${formatNumber(betData.rows.reduced)} (${betData.rows.reducedPercent}%)`
    }
  />,
];

// Systems or stake
const systemOrStake = betData => [
  <LabelCell
    key="label7"
    text={getText(
      betData.stake ? 'system-info-label-stake' : 'system-info-label-systems',
    )}
  />,
  <ValueCell
    key="value7"
    value={betData.stake ? betData.stake : formatNumber(betData.systems)}
  />,
];

// Turnover
const turnover = betData => [
  <LabelCell key="label8" text={getText('system-info-label-turnover')} />,
  <ValueCell key="value8" value={betData.formattedTurnover} />,
];

const BetTable = ({ betData, invalid, className, children }) => {
  // eslint-disable-line react/prop-types
  if (isEmpty(betData)) {
    return null;
  }

  return (
    <div className={classnames(className, { invalid: invalid })}>
      {children}
    </div>
  );
};

export const BarBetTable = ({ betData, invalid = false }) => (
  <BetTable
    betData={betData}
    invalid={invalid}
    className="betinfo-table header-bar"
  >
    <table>
      <tbody>
        <tr>
          {narrowRowsInfo(betData)}
          {unreducedRows(betData)}
          {coupons(betData)}
          {price(betData)}
        </tr>
        <tr>
          {narrowPrice(betData)}
          {reducedRows(betData)}
          {systemOrStake(betData)}
          {turnover(betData)}
        </tr>
      </tbody>
    </table>
  </BetTable>
);

BarBetTable.propTypes = {
  betData: object.isRequired,
  invalid: bool,
};

export const OneColumnPopupBetTable = ({ betData, invalid = false }) => (
  <BetTable
    betData={betData}
    invalid={invalid}
    className="betinfo-table one-column-popup"
  >
    <table>
      <tbody>
        <tr>{unreducedRows(betData)}</tr>
        <tr>{reducedRows(betData)}</tr>
        <tr>{coupons(betData)}</tr>
        <tr>{systemOrStake(betData)}</tr>
        <tr>{price(betData)}</tr>
        <tr>{turnover(betData)}</tr>
      </tbody>
    </table>
  </BetTable>
);

OneColumnPopupBetTable.propTypes = {
  betData: object.isRequired,
  invalid: bool,
};

export const TwoColumnPopupBetTable = ({ betData, invalid = false }) => (
  <BetTable
    betData={betData}
    invalid={invalid}
    className="betinfo-table two-column-popup"
  >
    <table>
      <tbody>
        <tr>
          {unreducedRows(betData)}
          {price(betData)}
        </tr>
        <tr>
          {reducedRows(betData)}
          {coupons(betData)}
        </tr>
        <tr>
          {systemOrStake(betData)}
          {turnover(betData)}
        </tr>
      </tbody>
    </table>
  </BetTable>
);

TwoColumnPopupBetTable.propTypes = {
  betData: object.isRequired,
  invalid: bool,
};
